import type { RouteObject } from 'react-router';
import { AdminLayout } from './components/AdminLayout';
import { BillingPage } from './pages/billing/BillingPage';
import { PlanVersionPage } from './pages/billing/PlanVersionPage';
import { BillingTab } from './pages/organization/BillingTab';
import { DomainsTab } from './pages/organization/DomainsTab';
import { PortalsTab } from './pages/organization/PortalsTab';
import { EntitlementsTab } from './pages/organization/EntitlementsTab';
import { InvitationsTab } from './pages/organization/InvitationsTab';
import { MembersTab } from './pages/organization/MembersTab';
import { ModulesTab } from './pages/organization/ModulesTab';
import { AuditTab } from './pages/organization/AuditTab';
import { OrganizationLayout } from './pages/organization/OrganizationLayout';
import { OverviewTab } from './pages/organization/OverviewTab';
import { SectionNotFound } from './pages/organization/SectionNotFound';
import { SupportAccessTab } from './pages/organization/SupportAccessTab';
import { OrganizationTicketsTab } from './pages/organization/OrganizationTicketsTab';
import { LabelsPage } from './pages/tickets/LabelsPage';
import { TicketPage } from './pages/tickets/TicketPage';
import { TicketSettingsPage } from './pages/tickets/TicketSettingsPage';
import { TicketsPage } from './pages/tickets/TicketsPage';
import { AuditPage } from './pages/AuditPage';
import { HealthPage } from './pages/HealthPage';
import { KeysPage } from './pages/KeysPage';
import { MySupportAccessPage } from './pages/MySupportAccessPage';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { ReleaseFlagsPage } from './pages/ReleaseFlagsPage';
import { StaffPage } from './pages/StaffPage';
import { NotFoundPage, PlaceholderPage } from './pages/StaticPages';

/**
 * Trace Admin's routes, all inside the staff shell. They are created only after DestinationGate confirmed
 * staff access, so no route ever renders for someone platform-api did not accept. Each section of an
 * organization is its own address, so a reload, a shared link and the back button keep it.
 */
export function createAdminRoutes(): RouteObject[] {
  return [
    {
      path: '/',
      element: <AdminLayout />,
      children: [
        { index: true, element: <HealthPage /> },
        { path: 'keys', element: <KeysPage /> },
        { path: 'tickets', element: <TicketsPage /> },
        // Static segments outrank :number, so labels and settings are never read as ticket numbers.
        { path: 'tickets/labels', element: <LabelsPage /> },
        { path: 'tickets/settings', element: <TicketSettingsPage /> },
        { path: 'tickets/:number', element: <TicketPage /> },
        { path: 'organizations', element: <OrganizationsPage /> },
        {
          path: 'organizations/:organizationId',
          element: <OrganizationLayout />,
          children: [
            { index: true, element: <OverviewTab /> },
            { path: 'modules', element: <ModulesTab /> },
            { path: 'entitlements', element: <EntitlementsTab /> },
            { path: 'billing', element: <BillingTab /> },
            { path: 'members', element: <MembersTab /> },
            { path: 'invitations', element: <InvitationsTab /> },
            { path: 'domains', element: <DomainsTab /> },
            { path: 'portals', element: <PortalsTab /> },
            { path: 'support-access', element: <SupportAccessTab /> },
            { path: 'tickets', element: <OrganizationTicketsTab /> },
            { path: 'audit', element: <AuditTab /> },
            { path: '*', element: <SectionNotFound /> },
          ],
        },
        { path: 'release-flags', element: <ReleaseFlagsPage /> },
        { path: 'staff', element: <StaffPage /> },
        { path: 'billing', element: <BillingPage /> },
        { path: 'billing/plan-versions/:versionId', element: <PlanVersionPage /> },
        { path: 'audit', element: <AuditPage /> },
        { path: 'support-access', element: <MySupportAccessPage /> },
        { path: 'jobs', element: <PlaceholderPage key="jobs" sectionId="jobs" /> },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ];
}
